export enum ErrorCode {
  DTO_VALIDATION_FAILED = 'DTO_VALIDATION_FAILED',
  WEBHOOK_VALIDATION_ERROR = 'WEBHOOK_VALIDATION_ERROR',
  ECOMMERCE_STORE_ALREADY_CONNECTED = 'ECOMMERCE_STORE_ALREADY_CONNECTED',
  OWNER_HAS_ACTIVE_CERTIFICATES = 'OWNER_HAS_ACTIVE_CERTIFICATES',
  // VERA-1095: the password sign-in path deliberately distinguishes "no
  // account for this email" from "wrong password" (accepted account-
  // enumeration tradeoff — see auth.service.ts loginWithPassword). This code
  // is the machine-readable signal the frontend branches on; the OTP path
  // stays intentionally generic and does NOT use this code.
  AUTH_ACCOUNT_NOT_FOUND = 'AUTH_ACCOUNT_NOT_FOUND',
  // VERA-1095: password sign-in against an account whose email WorkOS has
  // not verified (signup mints a WorkOS user with `emailVerified: false`,
  // and WorkOS itself refuses password auth until it flips true). Must be
  // distinguished from a genuinely wrong password — see auth.service.ts
  // loginWithPassword's isEmailNotVerifiedError check — so the frontend can
  // offer "verify your email" / resend instead of "wrong password".
  AUTH_EMAIL_NOT_VERIFIED = 'AUTH_EMAIL_NOT_VERIFIED',
  /**
   * VRG-438 — the invite would exceed the plan's seat allowance.
   *
   * A distinct CODE and not just a message, because the client does something
   * materially different with it: every other invite failure is an error to
   * report, this one is an upgrade path to offer. Branching on message text
   * would break the first time the wording changed.
   *
   * `details` carries `{used, limit, remaining, requested}` so the modal can
   * name the actual numbers rather than saying "your plan is full".
   */
  SEAT_LIMIT_REACHED = 'SEAT_LIMIT_REACHED',

  /**
   * VRG-455 — the ways an authenticated buyer's scan can fail to resolve a
   * recorded sale. They are SEPARATE CODES and not one, deliberately: the task's
   * own analysis names "one generic error for all three [causes of a claimed
   * certificate]" as the failure to avoid, and a client that has to branch on
   * message text breaks the first time the wording changes.
   *
   * ⚠️ THE USER-FACING COPY FOR THESE IS NOT DECIDED. Residuals 2 and 7 of the
   * task are open product questions — the no-match wording (and whether it
   * offers an order-email recovery path), and the three distinct causes behind
   * "entitlement valid, certificate already owned" (interception / fulfilment
   * error / deliberate misuse), which need different responses. The `ERRORS`
   * messages below are SERVER-SIDE strings for logs and Swagger, NOT sanctioned
   * UI copy. See `TODO(VRG-455)` at each throw site.
   */
  PENDING_SALE_NOT_FOUND = 'PENDING_SALE_NOT_FOUND',
  PENDING_SALE_CERTIFICATE_ALREADY_OWNED = 'PENDING_SALE_CERTIFICATE_ALREADY_OWNED',
  PENDING_SALE_CERTIFICATE_NOT_RELEASED = 'PENDING_SALE_CERTIFICATE_NOT_RELEASED',
  PENDING_SALE_TAG_HAS_NO_CERTIFICATE = 'PENDING_SALE_TAG_HAS_NO_CERTIFICATE',
  /**
   * VRG-455 — the record was consumed between this request reading it and
   * claiming it. Distinct from `PENDING_SALE_NOT_FOUND` because the buyer DID
   * hold a valid entitlement a moment ago; the honest client response is "try
   * again / this is already yours", not "we have no record of your purchase".
   */
  PENDING_SALE_ALREADY_RESOLVED = 'PENDING_SALE_ALREADY_RESOLVED',
  /**
   * VRG-455 — the caller's OWN record for this product exists but was claimed
   * earlier, so the claimable lookup (which filters to `pending`) cannot see it.
   *
   * Added because `PENDING_SALE_NOT_FOUND` was covering three situations at
   * once, and the settled copy for it — "We couldn't find a purchase linked to
   * your account… If you ordered with a different [email], enter it and we'll
   * send you a code" — is ACTIVELY WRONG for this one. It sends someone who
   * already owns the item down an email-recovery path that cannot help them and
   * implies they mistyped something.
   *
   * ⚠️ DISCLOSURE: this describes the CALLER'S OWN record and nothing else. It
   * says "you already claimed your purchase", never anything about a
   * certificate, a tag, or another person — which is exactly why it is safe to
   * distinguish from `NOT_FOUND` while `CERTIFICATE_ALREADY_OWNED` and
   * `CERTIFICATE_NOT_RELEASED` (which DO disclose a certificate's state) stay
   * behind a proven entitlement.
   *
   * Neighbour, not duplicate, of `PENDING_SALE_ALREADY_RESOLVED`: that one is a
   * lost compare-and-swap inside a single request (a double-tap racing itself,
   * "it is about to appear"), this one is a durable fact discovered before any
   * write ("you claimed this earlier; it is in your certificates"). If the copy
   * for the two ever converges, merging them is the right call — they are
   * separate today because the sentences are not the same.
   */
  PENDING_SALE_ALREADY_CLAIMED = 'PENDING_SALE_ALREADY_CLAIMED',
  /**
   * VRG-455 — the caller's own record was VOIDED (decision 1, requirement A:
   * a refund or cancellation retracts the sale without deleting the row, so it
   * survives as the audit of a retracted sale).
   *
   * Same reasoning as `ALREADY_CLAIMED`: without it a refunded buyer is told
   * their purchase cannot be found and is invited to try another email address,
   * which will never work because the record is not missing — it is retracted.
   * The brand is the only party who can explain a refund, so the copy sends
   * them there rather than into recovery.
   *
   * ⚠️ DISCLOSURE: the caller's own record again. Nothing about a certificate.
   */
  PENDING_SALE_VOIDED = 'PENDING_SALE_VOIDED',
  /**
   * VRG-455 — the scanned tag's certificate participates in a certificate GROUP
   * (VRG-231), and which certificate a pending-ownership entitlement should
   * bind to in that case is an OPEN PRODUCT DECISION, not an implementation
   * detail this endpoint may invent.
   *
   * For an indivisible group a child's scan redirects to the PARENT, so a
   * buyer who scans a child tag could defensibly be resolving against either
   * the child (the certificate the tag is actually bound to) or the parent
   * (what the scan resolves to, and what "sold only as a group" implies they
   * bought). A pending record is keyed on a single product; a bundle spans
   * several, so it maps cleanly to neither.
   *
   * Refusing loudly is the only honest option: silently falling through to
   * ordinary self-claim, or picking a side, would ship a guess as behaviour.
   * TODO(VRG-455): remove this refusal once VRG-231 settles group semantics.
   */
  PENDING_SALE_GROUPED_CERTIFICATE_UNSUPPORTED = 'PENDING_SALE_GROUPED_CERTIFICATE_UNSUPPORTED',
  // An SMS second-factor code send was refused by a cooldown / rate limit.
  SMS_CODE_RATE_LIMITED = 'SMS_CODE_RATE_LIMITED',
  // A security code could not be texted because the number replied STOP (Twilio 21610).
  // `details` carries `maskedPhone` and `senderNumber` (null when not configured).
  SMS_RECIPIENT_OPTED_OUT = 'SMS_RECIPIENT_OPTED_OUT',
  // A platform session is gone (signed out, revoked, idle or past its absolute cap).
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  // A renewal presented a refresh token the server cannot place (e.g. one another tab has
  // already rotated past). Not necessarily the end of the session; the cookie is kept.
  SESSION_UNKNOWN = 'SESSION_UNKNOWN',
  // Adding another second factor needs a fresh step-up with an existing one first.
  STEP_UP_REQUIRED = 'STEP_UP_REQUIRED',
  // Sign-in refused: the account has been deactivated by Vera staff.
  ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED',
  /**
   * VRG-719 — a file could not be WRITTEN to storage (GCS refused the write, the disk was
   * full, ...). A server-side fault, never the caller's: it answers 503, not 400.
   *
   * It used to surface as `FileGeneralError` → 400 "Client error occurred", logged at LOG
   * level with the bucket path as the message. So a prod-wide outage of every upload (the
   * storage service account had lost its bucket grant) looked like a run of bad requests
   * and never reached an error-level log. `details` carries `{[field]: string[]}` when the
   * write belonged to a known form field (product `images`), so the client can show the
   * reason inline under that field instead of a generic toast.
   */
  FILE_STORAGE_UNAVAILABLE = 'FILE_STORAGE_UNAVAILABLE',
}

export const ERRORS = {
  [ErrorCode.DTO_VALIDATION_FAILED]: {
    code: ErrorCode.DTO_VALIDATION_FAILED,
    message: 'Validation errors',
  },
  [ErrorCode.WEBHOOK_VALIDATION_ERROR]: {
    code: ErrorCode.WEBHOOK_VALIDATION_ERROR,
    message: 'Webhook validation error',
  },
  [ErrorCode.ECOMMERCE_STORE_ALREADY_CONNECTED]: {
    code: ErrorCode.ECOMMERCE_STORE_ALREADY_CONNECTED,
    message: 'This store is already connected to another company',
  },
  [ErrorCode.OWNER_HAS_ACTIVE_CERTIFICATES]: {
    code: ErrorCode.OWNER_HAS_ACTIVE_CERTIFICATES,
    message: 'Owner has active certificates and cannot be deleted',
  },
  [ErrorCode.AUTH_ACCOUNT_NOT_FOUND]: {
    code: ErrorCode.AUTH_ACCOUNT_NOT_FOUND,
    message: 'No account exists for this email address',
  },
  [ErrorCode.AUTH_EMAIL_NOT_VERIFIED]: {
    code: ErrorCode.AUTH_EMAIL_NOT_VERIFIED,
    message: 'Verify your email address before signing in with a password',
  },
  [ErrorCode.SEAT_LIMIT_REACHED]: {
    code: ErrorCode.SEAT_LIMIT_REACHED,
    message: 'This invite would exceed the seats included in your plan',
  },
  // VRG-455 — server-side strings only. TODO(VRG-455): the user-facing wording
  // for all five is an open product decision (residuals 2 and 7); do NOT render
  // these verbatim.
  [ErrorCode.PENDING_SALE_NOT_FOUND]: {
    code: ErrorCode.PENDING_SALE_NOT_FOUND,
    message: 'No recorded sale of this product to this buyer',
  },
  [ErrorCode.PENDING_SALE_CERTIFICATE_ALREADY_OWNED]: {
    code: ErrorCode.PENDING_SALE_CERTIFICATE_ALREADY_OWNED,
    message: 'This certificate already belongs to someone else',
  },
  [ErrorCode.PENDING_SALE_CERTIFICATE_NOT_RELEASED]: {
    code: ErrorCode.PENDING_SALE_CERTIFICATE_NOT_RELEASED,
    message: 'This item is retail or held stock and cannot be claimed',
  },
  [ErrorCode.PENDING_SALE_TAG_HAS_NO_CERTIFICATE]: {
    code: ErrorCode.PENDING_SALE_TAG_HAS_NO_CERTIFICATE,
    message: 'This tag has no certificate',
  },
  [ErrorCode.PENDING_SALE_ALREADY_RESOLVED]: {
    code: ErrorCode.PENDING_SALE_ALREADY_RESOLVED,
    message: 'This recorded sale has already been claimed',
  },
  [ErrorCode.PENDING_SALE_ALREADY_CLAIMED]: {
    code: ErrorCode.PENDING_SALE_ALREADY_CLAIMED,
    message: 'This buyer already claimed their recorded sale of this product',
  },
  [ErrorCode.PENDING_SALE_VOIDED]: {
    code: ErrorCode.PENDING_SALE_VOIDED,
    message: 'The recorded sale of this product to this buyer was voided',
  },
  [ErrorCode.PENDING_SALE_GROUPED_CERTIFICATE_UNSUPPORTED]: {
    code: ErrorCode.PENDING_SALE_GROUPED_CERTIFICATE_UNSUPPORTED,
    message:
      'This item is part of a certificate group and cannot be claimed this way yet',
  },
  [ErrorCode.SMS_CODE_RATE_LIMITED]: {
    code: ErrorCode.SMS_CODE_RATE_LIMITED,
    message: 'Too many verification texts. Wait a moment before requesting another.',
  },
  [ErrorCode.SMS_RECIPIENT_OPTED_OUT]: {
    code: ErrorCode.SMS_RECIPIENT_OPTED_OUT,
    message:
      "We can't text this number because it replied STOP. Reply START to any message from Vera to turn texts back on, or use another sign-in method.",
  },
  [ErrorCode.SESSION_EXPIRED]: {
    code: ErrorCode.SESSION_EXPIRED,
    message: 'Your session has ended. Sign in again.',
  },
  [ErrorCode.ACCOUNT_DEACTIVATED]: {
    code: ErrorCode.ACCOUNT_DEACTIVATED,
    message: 'This account has been deactivated. Contact your administrator or Vera support.',
  },
  [ErrorCode.STEP_UP_REQUIRED]: {
    code: ErrorCode.STEP_UP_REQUIRED,
    message: 'Confirm with your existing verification method first.',
  },
  [ErrorCode.SESSION_UNKNOWN]: {
    code: ErrorCode.SESSION_UNKNOWN,
    message: 'This session could not be renewed from this tab.',
  },
  [ErrorCode.FILE_STORAGE_UNAVAILABLE]: {
    code: ErrorCode.FILE_STORAGE_UNAVAILABLE,
    message: "We couldn't save your files right now, so nothing was saved. Try again in a few minutes.",
  },
};

export function createSwaggerErrorCodesDescription(codes: ErrorCode[]): string {
  const description = ['Possible error codes:'];

  for (const code of codes) {
    const data = ERRORS[code];

    description.push(`<code>"code": "${code}"</code> - ${data.message}`);
  }

  return description.join('\n\n');
}
